import React, { CSSProperties } from "react";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { parseTime, Time, TimeInterval } from "./time_utils";
import { Container, Row } from "../components";
import { Picker } from "./picker";
import { useI18n } from "../lib/i18n/i18n_provider";

interface TimeSlot {
  startTime: string;
  endTime: string[];
}
interface TimeRangePickerV2Props {
  value?: Partial<TimeInterval> | null;
  onChange?: (value: Partial<TimeInterval>) => void;
  disabled?: boolean;
  timeslots: TimeSlot[];
  override?: {
    root?: { style?: CSSProperties };
    view?: { style?: CSSProperties };
    arrow?: { style?: CSSProperties };
  };
}

export function TimeRangePickerV2(props: TimeRangePickerV2Props) {
  const { onChange, value, disabled, override, timeslots } = props;
  const { start, end } = value || {};
  const { fbt } = useI18n();
  const handleStartTimeChange = React.useCallback(
    (time: Time) => {
      if (!onChange) {
        return;
      }

      const startTimeSlots = timeslots.find((t) => t.startTime === time);
      const hasMatchingEndTime = startTimeSlots?.endTime.find((e) => e === end);

      onChange({
        start: time,
        end: hasMatchingEndTime ? end : startTimeSlots?.endTime[0],
      });
    },
    [end, onChange, timeslots]
  );

  const handleEndTimeChange = React.useCallback(
    (time: Time) => {
      if (!onChange) {
        return;
      }

      onChange({
        start,
        end: time,
      });
    },
    [start, onChange]
  );

  const startTimeOptions = timeslots.map((t) => ({
    value: t.startTime,
    label: fbt.formatTime(parseTime(t.startTime)),
  }));
  const startTimeSlots = timeslots.find((t) => t.startTime === start);

  const endTimeOptions = startTimeSlots
    ? startTimeSlots.endTime.map((t) => ({
        value: t,
        label: fbt.formatTime(parseTime(t)),
      }))
    : [];

  return (
    <Row alignItems="center">
      <Container flex={1}>
        <Picker
          placeholder={fbt("Start time")}
          value={start}
          onChange={handleStartTimeChange}
          options={startTimeOptions}
          disabled={disabled}
          override={{
            ...override,
            view: {
              style: {
                paddingLeft: 8,
                paddingRight: 8,
                borderRadius: 5,
                border: "1px solid #D7D5DD",
                ...override?.view?.style,
              },
            },
          }}
        />
      </Container>
      <Spacer size={8} direction="horizontal" />
      <Text>{fbt("to")}</Text>
      <Spacer size={8} direction="horizontal" />
      <Container flex={1}>
        <Picker
          placeholder={fbt("End time")}
          value={end}
          disabled={!start}
          options={endTimeOptions}
          onChange={handleEndTimeChange}
          override={{
            ...override,
            view: {
              style: {
                paddingLeft: 8,
                paddingRight: 8,
                borderRadius: 5,
                border: "1px solid #D7D5DD",
                ...override?.view?.style,
              },
            },
          }}
        />
      </Container>
    </Row>
  );
}
